<template>
  <v-container>
    <!-- Header Section -->
    <v-row justify="space-between" align="center">
      <v-col cols="12" md="6" lg="8">
        <!-- @slot header -->
        <slot name="header" />
      </v-col>

      <v-col cols="12" md="6" lg="4">
        <v-row justify-md="end" align="center" no-gutters>
          <v-col cols="auto" class="d-flex flex-wrap align-center gap-3">
            <!-- @slot header-actions -->
            <slot name="header-actions" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Filter & Search Section -->
    <v-row justify="space-between" align="center">
      <!-- @slot filter -->
      <v-col cols="12" md="8" lg="7" class="filter-wrapper">
        <slot name="filter">
          <div></div>
        </slot>
      </v-col>

      <!-- @slot search -->
      <v-col cols="12" md="4">
        <slot name="search">
          <div></div>
        </slot>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="show_loading_state" class="justify-center">
      <v-col cols="12" class="text-center mt-10">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <!-- Content Grid or Empty State -->
    <v-row v-if="!show_loading_state" class="mt-4 mb-12">
      <!-- @slot grid -->
      <slot name="grid">
        <v-col cols="12" class="text-center">
          <!-- @slot empty-state - shown when there are no items to display -->
          <slot name="empty-state">
            <empty-state
              icon="mdi-package-variant"
              title="No Items Found"
              description="Try adjusting your search or filter criteria"
            />
          </slot>
        </v-col>
      </slot>
    </v-row>

    <!-- Additional Content e.g. dialogs -->
    <!-- @slot additional-content -->
    <slot name="additional-content" />
  </v-container>
</template>

<script>
/**
 * CardGridWithControls
 *
 * Responsive layout component for displaying card-based grids with customizable controls and states.
 * Provides a consistent structure for pages that display collections of items in a card format.
 *
 * Features:
 * - Header section with optional actions (buttons, filters, etc.)
 * - Dual control sections for search and filters
 * - Automatic handling of loading and empty states
 * - Grid slot for card content with automatic empty state fallback
 * - Additional content slot for dialogs and modals
 *
 * @example
 * <card-grid-with-controls :show_loading_state="loading">
 *   <template #header>
 *     <h1>My Items</h1>
 *   </template>
 *
 *   <template #header-actions>
 *     <v-btn>Create New</v-btn>
 *   </template>
 *
 *   <template #filter>
 *    <v-btn-toggle ... />
 *   </template>
 *
 *   <template #search>
 *     <search-input v-model="search" />
 *   </template>
 *
 *   <template #grid>
 *     <v-col v-for="item in items" :key="item.id">
 *       <item-card :item="item" />
 *     </v-col>
 *   </template>
 *
 *   <template #empty-state>
 *     <empty-state title="No items found" description="..." />
 *   </template>
 * </card-grid-with-controls>
 */
export default {
  name: 'CardGridWithControls',

  components: {
    EmptyState: () => import('@/components/global/empty-state.vue'),
  },

  props: {
    /**
     * Controls whether to display the loading spinner.
     * When true, shows a centered progress indicator and hides other content.
     */
    show_loading_state: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-wrapper {
  max-width: 100%;
  overflow-x: auto;
}
</style>
